import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Title, Text, TextInput, Button, Group, Stack,
  Accordion, Checkbox, Alert, Grid, Divider
} from '@mantine/core';
import PhoneInputField from '../../../components/PhoneInputField/PhoneInputField';
import { IconAlertCircle, IconCheck, IconArrowLeft, IconSparkles } from '@tabler/icons-react';
import { useAuth } from '../../../contexts/AuthContext';
import useSchool from '../../../hooks/useSchool';

const inputStyles = { label: { textAlign: 'left' }, description: { textAlign: 'left' } };

// The form keeps readable teacher_*/school_* names; the columns are contact_*
// and bare names on `schools`. Mapping happens here, at the boundary.
const toSchoolRow = (f) => ({
  name: f.school_name.trim(),
  website: f.school_website.trim() || null,
  email: f.school_email.trim() || null,
  phone: f.school_phone || null,
  city: f.school_city.trim(),
  province: f.school_province.trim(),
  country: f.school_country.trim() || 'Canada',
  contact_first_name: f.teacher_first_name.trim(),
  contact_last_name: f.teacher_last_name.trim(),
  contact_email: f.teacher_email.trim(),
  contact_phone: f.teacher_phone,
});

const SchoolSetupForm = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    school, seasons, currentSeason, activeYear, saving,
    saveSchoolProfile, signWaiver, hasSchool,
  } = useSchool();

  // School exists but hasn't signed up for the active year yet — a returning
  // teacher. Details are already theirs; the waiver is the only real work.
  const isReturning = hasSchool && !currentSeason;

  const [formData, setFormData] = useState({
    teacher_first_name: '',
    teacher_last_name: '',
    teacher_email: '',
    teacher_phone: '',
    school_name: '',
    school_website: '',
    school_city: '',
    school_province: '',
    school_country: 'Canada',
    school_phone: '',
    school_email: '',
    waiver_agreed: false,
    waiver_signer_name: ''
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  // Pre-fill from the existing school, or from the auth user on first visit.
  useEffect(() => {
    if (school) {
      // The waiver is per-season and deliberately NOT carried over: a 2026
      // signature doesn't cover a 2027 roster. The signer's name is prefilled
      // from their last signature as a convenience; the box stays unchecked.
      const lastSigner = seasons.find(s => s.waiver_signer_name)?.waiver_signer_name || '';
      setFormData({
        teacher_first_name: school.contact_first_name || '',
        teacher_last_name: school.contact_last_name || '',
        teacher_email: school.contact_email || user?.email || '',
        teacher_phone: school.contact_phone || '',
        school_name: school.name || '',
        school_website: school.website || '',
        school_city: school.city || '',
        school_province: school.province || '',
        school_country: school.country || 'Canada',
        school_phone: school.phone || '',
        school_email: school.email || '',
        waiver_agreed: currentSeason?.waiver_agreed || false,
        waiver_signer_name: currentSeason?.waiver_signer_name || lastSigner
      });
    } else if (user?.email) {
      setFormData(prev => ({ ...prev, teacher_email: user.email }));
    }
  }, [school, seasons, currentSeason, user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.teacher_first_name.trim()) newErrors.teacher_first_name = 'Required';
    if (!formData.teacher_last_name.trim()) newErrors.teacher_last_name = 'Required';
    if (!formData.teacher_email.trim()) newErrors.teacher_email = 'Required';
    if (!formData.teacher_phone) newErrors.teacher_phone = 'Required';
    if (!formData.school_name.trim()) newErrors.school_name = 'Required';
    if (!formData.school_city.trim()) newErrors.school_city = 'Required';
    if (!formData.school_province.trim()) newErrors.school_province = 'Required';
    if (!formData.waiver_agreed) newErrors.waiver_agreed = 'You must agree to all terms to continue';
    if (!formData.waiver_signer_name.trim()) newErrors.waiver_signer_name = 'Please type your full name as signature';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const isFirstTime = !hasSchool;

    // Two writes to two tables: the school is permanent, the waiver is this
    // season's. Profile first — the waiver needs a school id to hang off.
    const savedSchool = await saveSchoolProfile(toSchoolRow(formData));
    if (!savedSchool) {
      // Includes the trigger's 23505 when this account already has a school.
      setSubmitError('Failed to save your school details. Please try again.');
      return;
    }

    const season = await signWaiver(
      { waiver_signer_name: formData.waiver_signer_name.trim() },
      savedSchool.id
    );
    if (!season) {
      setSubmitError(`Your school details were saved, but the ${activeYear} waiver didn't go through. Please try again.`);
      return;
    }

    if (isFirstTime) {
      // Tells PDC a school is waiting for review. Never blocks the teacher —
      // review is advisory and they can add students right now either way.
      fetch('/api/notify-school-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schoolId: savedSchool.id }),
      }).catch(() => {});
    }

    navigate('/register/school/students');
  };

  const heading = isReturning
    ? `Prix du Canada ${activeYear} Sign-Up`
    : hasSchool ? 'Studio Details' : 'Studio Account Setup';

  const subheading = isReturning
    ? 'Your school details are already saved — check them over and sign this season\'s waiver.'
    : 'This information is collected once and reused for every season.';

  return (
    <div style={{ background: 'linear-gradient(135deg, #faf9f7 0%, #f5f3f0 100%)', minHeight: '100vh', padding: '2rem 1rem' }}>
      <Container size="md">
        <Group justify="space-between" mb="md">
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/register')}
            size="sm"
          >
            Back
          </Button>
        </Group>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title order={2} style={{ color: 'var(--primary-gold)', fontFamily: "'Playfair Display', serif" }}>
            {heading}
          </Title>
          <Text color="dimmed" mt="xs">{subheading}</Text>
        </div>

        {isReturning && !readOnly && (
          <Alert
            icon={<IconSparkles size={18} />}
            color="yellow"
            variant="light"
            mb="xl"
            title={`Welcome back, ${school?.name}`}
            styles={{ body: { textAlign: 'left' } }}
          >
            <Text size="sm">
              Nothing to retype — everything below is what you gave us last time. Update anything
              that's changed, sign the {activeYear} waiver, and you're straight into your roster.
            </Text>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <Stack spacing="xl">

            {/* Teacher Information */}
            <Paper p="xl" radius="md" withBorder style={{ borderTop: '4px solid var(--primary-gold)' }}>
              <Title order={4} mb="lg" style={{ color: 'var(--charcoal)' }}>Teacher / Director Information</Title>
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="First Name"
                    placeholder="Jane"
                    required
                    value={formData.teacher_first_name}
                    onChange={(e) => handleChange('teacher_first_name', e.target.value)}
                    error={errors.teacher_first_name}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Last Name"
                    placeholder="Smith"
                    required
                    value={formData.teacher_last_name}
                    onChange={(e) => handleChange('teacher_last_name', e.target.value)}
                    error={errors.teacher_last_name}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Email"
                    placeholder="jane@studio.com"
                    type="email"
                    required
                    value={formData.teacher_email}
                    onChange={(e) => handleChange('teacher_email', e.target.value)}
                    error={errors.teacher_email}
                    styles={inputStyles}
                    description="Used as primary contact for your school registration"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <PhoneInputField
                    label="Phone Number"
                    required
                    value={formData.teacher_phone}
                    onChange={(v) => handleChange('teacher_phone', v)}
                    error={errors.teacher_phone}
                    styles={inputStyles}
                    description="Will be used as default emergency contact for students"
                  />
                </Grid.Col>
              </Grid>
            </Paper>

            {/* School Information */}
            <Paper p="xl" radius="md" withBorder style={{ borderTop: '4px solid var(--primary-gold)' }}>
              <Title order={4} mb="lg" style={{ color: 'var(--charcoal)' }}>School Information</Title>
              <Grid gutter="md">
                <Grid.Col span={12}>
                  <TextInput
                    label="School / Studio Name"
                    placeholder="Academy of Dance"
                    required
                    value={formData.school_name}
                    onChange={(e) => handleChange('school_name', e.target.value)}
                    error={errors.school_name}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Website"
                    placeholder="https://mystudio.com"
                    value={formData.school_website}
                    onChange={(e) => handleChange('school_website', e.target.value)}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="School Email"
                    placeholder="info@mystudio.com"
                    type="email"
                    value={formData.school_email}
                    onChange={(e) => handleChange('school_email', e.target.value)}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="City"
                    placeholder="Toronto"
                    required
                    value={formData.school_city}
                    onChange={(e) => handleChange('school_city', e.target.value)}
                    error={errors.school_city}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="Province"
                    placeholder="Ontario"
                    required
                    value={formData.school_province}
                    onChange={(e) => handleChange('school_province', e.target.value)}
                    error={errors.school_province}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <TextInput
                    label="Country"
                    value={formData.school_country}
                    onChange={(e) => handleChange('school_country', e.target.value)}
                    styles={inputStyles}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <PhoneInputField
                    label="School Phone"
                    value={formData.school_phone}
                    onChange={(v) => handleChange('school_phone', v)}
                    styles={inputStyles}
                  />
                </Grid.Col>
              </Grid>
            </Paper>

            {/* School Waiver — per season */}
            <Paper p="xl" radius="md" withBorder style={{ borderTop: '4px solid var(--primary-gold)' }}>
              <Title order={4} mb="xs" style={{ color: 'var(--charcoal)' }}>
                {activeYear} School Waiver &amp; Agreements
              </Title>
              <Text color="dimmed" size="sm" mb="lg">
                Please read and agree to all terms on behalf of your school and its students.
                {hasSchool && ' This is signed fresh each season, since it covers the students you enter this year.'}
              </Text>

              <Accordion variant="separated" mb="xl">
                <Accordion.Item value="liability">
                  <Accordion.Control>
                    <Text fw={600}>Liability Waiver</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" color="dimmed" style={{ lineHeight: 1.7 }}>
                      The school, its directors, teachers, and representatives acknowledge and accept that dance performance
                      and competition activities carry inherent risks of physical injury. By registering students through
                      this portal, the school agrees to:
                      <ul style={{ marginTop: '0.5rem' }}>
                        <li>Ensure all registered students are physically capable of participating</li>
                        <li>Hold Prix du Canada, its organizers, staff, and venue harmless from any injury, loss, or damage</li>
                        <li>Confirm that all students have appropriate health insurance coverage</li>
                        <li>Take responsibility for the safety and supervision of students in their care</li>
                      </ul>
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="media">
                  <Accordion.Control>
                    <Text fw={600}>Media Release</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" color="dimmed" style={{ lineHeight: 1.7 }}>
                      All registered students will be <strong>professionally filmed and photographed</strong> during the competition.
                      <ul style={{ marginTop: '0.5rem' }}>
                        <li>A complimentary professional performance video will be provided to each student's family at no charge</li>
                        <li>Prix du Canada reserves the right to use performance footage for promotional purposes (website, social media, marketing)</li>
                        <li>Students or families who wish to opt out of promotional media use (but not the performance video) must notify us in writing</li>
                        <li>The school is responsible for obtaining individual parent/guardian consent for minor students (handled via the consent email in this portal)</li>
                      </ul>
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="conduct">
                  <Accordion.Control>
                    <Text fw={600}>Code of Conduct</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" color="dimmed" style={{ lineHeight: 1.7 }}>
                      The school and all its representatives agree to:
                      <ul style={{ marginTop: '0.5rem' }}>
                        <li>Conduct themselves and their students with professionalism and respect toward all participants, judges, and staff</li>
                        <li>Ensure students are appropriately attired and presented for competition</li>
                        <li>Comply with all competition rules, schedules, and venue guidelines</li>
                        <li>Accept judges' decisions as final</li>
                        <li>Understand that violations may result in disqualification</li>
                      </ul>
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>

              <Stack spacing="md">
                <Checkbox
                  label={
                    <Text size="sm">
                      I have read and agree to the <strong>Liability Waiver</strong>, <strong>Media Release</strong>,
                      and <strong>Code of Conduct</strong> on behalf of {formData.school_name || 'my school'} and all
                      students we register for {activeYear}.
                    </Text>
                  }
                  checked={formData.waiver_agreed}
                  onChange={(e) => handleChange('waiver_agreed', e.currentTarget.checked)}
                  styles={{ label: { textAlign: 'left', cursor: 'pointer' } }}
                />

                {formData.waiver_agreed && (
                  <>
                    <Divider />
                    <TextInput
                      label="Electronic Signature (Type your full name)"
                      placeholder="Jane Smith"
                      description={`Signed on behalf of ${formData.school_name || 'the school'} on ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}`}
                      value={formData.waiver_signer_name}
                      onChange={(e) => handleChange('waiver_signer_name', e.target.value)}
                      error={errors.waiver_signer_name}
                      styles={inputStyles}
                    />
                  </>
                )}

                {errors.waiver_agreed && (
                  <Alert color="red" icon={<IconAlertCircle size="1rem" />}>
                    <Text size="sm">{errors.waiver_agreed}</Text>
                  </Alert>
                )}
              </Stack>
            </Paper>

            {submitError && (
              <Alert color="red" icon={<IconAlertCircle size="1rem" />}>
                <Text size="sm" style={{ textAlign: 'left' }}>{submitError}</Text>
              </Alert>
            )}

            {!readOnly && (
              <Group justify="flex-end">
                <Button
                  type="submit"
                  size="lg"
                  loading={saving}
                  leftSection={<IconCheck size={18} />}
                  style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
                >
                  {currentSeason
                    ? 'Save Changes & Return to Students'
                    : `Sign & Continue to Student Registration`}
                </Button>
              </Group>
            )}
            {readOnly && (
              <Group justify="flex-end">
                <Button
                  size="lg"
                  variant="light"
                  onClick={() => navigate('/register/school/students')}
                >
                  Back to Students
                </Button>
              </Group>
            )}

          </Stack>
          </fieldset>
        </form>
      </Container>
    </div>
  );
};

export default SchoolSetupForm;
